import { ReactNode } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import * as K from 'common/constants';
import ELEMENT, { DynamicTestId } from 'common/element';

import { setMark } from 'common/PerformanceLogger';
import { isRegisteredRoute, normalizeLink } from 'common/util/location';

export interface LinkProps {
  children:      ReactNode;
  href?:         string;
  className?:    string;
  openInNewTab?: boolean;
  target?:       string;
  testId?:       DynamicTestId;
  onClick?:      () => void;
  onHover?:      () => void;
  data?:         Record<string, string | number | boolean>,
}

/**
 * Link is a component for implementing a Scalyr version of the Link component from the react-router library.
 * Currently, this is a thin wrapper component whose core responsibility is to check the route component of the "href"
 * prop against the reactRoutes whitelist in the cache, and then return either a Link component for navigation within
 * the React SPA or an anchor tag to forward the request to the server (which will presumably serve the angular app).
 *
 * Required:
 *
 * @prop children        The link content
 * @prop href            A string representation of the link location
 * @prop className       Class to apply to the link
 * @prop openInNewTab    If true, the link target will open in a new browser tab
 * @prop target          Link target; BS components that render as Link cannot pass a non-DOM property such as 'openInNewTab'
 * @prop testId          uniquely identifying string to use for testing
 * @prop onClick         function to run when link is clicked
 * @prop onHover         function to run when link is hovered over
 * @prop data            additional data properties
 *
 * @returns {Component}
 */

function Link({
  children,
  href = '',
  className,
  openInNewTab,
  target,
  testId,
  onClick,
  onHover,
  data = {},
}: LinkProps): JSX.Element {


  // We are using window object here instead of settings recoil state as it was causing a render warning for the Link rendered in TimelineLegend button component


  const reactPaths = window.reactPaths;
  const reactPathsBlacklist = window.reactPathsBlacklist;

  const normalizedHref = normalizeLink(href);

  let link = (
    <a
      href={normalizedHref}
      className={className}
      data-test-id={testId}
      target={openInNewTab ? '_blank' : target}
      onClick={onClick}
      onMouseEnter={onHover}
      {...data}
    >
      {children}
    </a>
  );

  // if the route is a known registered or public route, make the link internal to the app
  const location = new URL(normalizedHref, window.location.href) as unknown as Location;
  if (isRegisteredRoute(location, reactPaths, reactPathsBlacklist) || isRegisteredRoute(location, Object.values(K.PUBLIC_ROUTE))) {
    link = (
      <ReactRouterLink
        to={normalizedHref}
        className={className}
        data-test-id={testId}
        target={openInNewTab ? '_blank' : target}
        onClick={onClick}
        onMouseEnter={onHover}
        {...data}
        >
        {children}
      </ReactRouterLink>
    );
  }

  // Set performance marker for page navigation performance monitoring
  return <span onClick={(): void => setMark('linkClick', { from: window.location.href, to: normalizedHref, target: openInNewTab ? '_blank' : target })} role="link" tabIndex={0} data-test-id={ELEMENT.linkPerformanceMarker}>{link}</span>;
}

export default Link;
